<script lang="ts">
  /**
   * Platzhalterzeilen statt drehendem Rad.
   *
   * ## Drei Dinge, die er können muss
   *
   * Der Tagesplan hatte eine Ladeanzeige ohne Abbruchbedingung: Blieb die
   * Antwort aus, drehte sich das Rad weiter. **Ein Ladezustand ohne Ende ist
   * schlimmer als ein Fehler**, weil er den Nutzer am Warten hält, statt ihn
   * handeln zu lassen.
   *
   * 1. **Was lädt** — `text` ist Pflicht, nicht Zierde. „lädt …" allein sagt
   *    nichts; „sucht Schlafplätze im Umkreis …" sagt, worauf man wartet.
   * 2. **Wie lange schon** — ab `geduld` Sekunden erscheint eine zweite Zeile.
   * 3. **Was tun, wenn es hängt** — `onabbrechen` erscheint mit dieser Zeile.
   *    Ohne Ausweg wäre die Zeile nur eine Entschuldigung.
   *
   * Die Platzhalterzeilen sind ruhig: geblockte Flächen in `--surface-2`, keine
   * Farbe, keine Animation. Auf iOS zeigt die App ohnehin **immer zuerst** den
   * gespeicherten Stand; dort erscheint ein Ladezustand nur, wo es wirklich noch
   * nichts gibt.
   *
   * ## Anders als in der Vorlage
   *
   * `text` ist hier auch im Typ Pflicht (in der Vorlage stand es als optional,
   * obwohl der Dateikopf es Pflicht nennt). Eine Pflicht, die der Typ nicht
   * trägt, ist eine Bitte — und die erste Verwendung ohne Text wäre wieder
   * „lädt …".
   */
  import { dauertLaenger, GEDULD_SEKUNDEN } from './muster';

  interface Props {
    zeilen?: number;
    /** Pflicht: WAS lädt. „sucht Schlafplätze im Umkreis …", nicht „lädt …". */
    text: string;
    /** Sekunden, die schon vergangen sind — falls außerhalb gezählt wird. */
    seit?: number | null;
    /** Nach wie vielen Sekunden „Das dauert länger als erwartet." erscheint. */
    geduld?: number;
    /** Der Ausweg. Ohne ihn ist die Geduldszeile nur eine Entschuldigung. */
    onabbrechen?: () => void;
  }
  let {
    zeilen = 3,
    text,
    seit = null,
    geduld = GEDULD_SEKUNDEN,
    onabbrechen
  }: Props = $props();

  let verstrichen = $state(false);

  /**
   * Die Uhr beginnt neu, wenn sich `text` ändert: Ein neuer Text heißt, dass
   * etwas anderes lädt — die Geduld des Nutzers fängt dann von vorn an.
   */
  $effect(() => {
    // `text` wird mitgelesen, damit der Effekt auch dann neu läuft, wenn nur
    // er sich ändert — die beiden zusammen sind der „Anlass" des Wartens.
    const anlass = { was: text, dauer: geduld * 1000 };
    verstrichen = false;
    const uhr = setTimeout(() => (verstrichen = true), anlass.dauer);
    return () => clearTimeout(uhr);
  });

  const lange = $derived(dauertLaenger(verstrichen, seit, geduld));
</script>

<div class="laden" aria-busy="true" aria-live="polite">
  <span class="was">{text}</span>

  {#each Array.from({ length: zeilen }), i}
    <div class="platzhalter" aria-hidden="true">
      <span class="punkt"></span>
      <span class="balken">
        <!-- Von Zeile zu Zeile kürzer: Ein Block aus gleich langen Balken sieht
             aus wie eine Tabelle, ungleiche sehen aus wie Text. -->
        <span class="oben" style:width="{70 - i * 12}%"></span>
        <span class="unten" style:width="{50 - i * 8}%"></span>
      </span>
    </div>
  {/each}

  {#if lange}
    <div class="geduld">
      <span>Das dauert länger als erwartet.</span>
      {#if onabbrechen}
        <button type="button" onclick={onabbrechen}>abbrechen</button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .laden {
    display: grid;
    gap: var(--raum-4);
  }

  .was {
    font-size: var(--fs-text);
    color: var(--text-dim);
  }

  .platzhalter {
    display: flex;
    gap: var(--raum-5);
    align-items: center;
  }

  .punkt {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--surface-2);
    flex: none;
  }

  .balken {
    display: grid;
    gap: var(--raum-2);
    flex: 1;
  }
  .oben,
  .unten {
    border-radius: var(--radius-rund);
    background: var(--surface-2);
  }
  .oben {
    height: 10px;
  }
  .unten {
    height: 8px;
  }

  .geduld {
    display: flex;
    align-items: baseline;
    gap: var(--raum-5);
    flex-wrap: wrap;
    font-size: var(--fs-fein);
    color: var(--text-dim);
  }

  .geduld button {
    border: 0;
    background: none;
    padding: 0;
    color: var(--accent);
    font: inherit;
    font-size: var(--fs-fein);
    text-decoration: underline;
    cursor: pointer;
  }
</style>
